import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';

registerLocale('fr-FR', fr);
import Button from '../../../../button';
import Modalheader from '../../../components/header';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';
import ApiDatabase from 'server';
import StyledInput from '../../../../styledInput/StyledInput';
import { dateToIsoString } from '../../../../../helper/helper';

const ModalMissionInterrupt = () => {
  const { toggle } = useModal();
  const { objID, setModalDataReturn, updateMissionStatusLocal } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonSelected, setReasonSelected] = useState('');
  const [endDate, setEndDate] = useState(new Date());
  const [duplicateMission, setDuplicateMission] = useState(false);
  const [duplicateDateStart, setDuplicateDateStart] = useState(new Date());
  const [duplicateDateEnd, setDuplicateDateEnd] = useState(new Date());
  const [missionData, setMissionData] = useState(null);
  const [isWaiting, setIsWaiting] = useState(true);
  const [isStartToday, setIsStartToday] = useState(false);
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    if (token && objID) {
      setIsWaiting(true);
      ApiDatabase.getMissionDetailsById(
        { token, idMission: objID },
        (data) => {
          setMissionData(data);
          if (data?.duration?.dateend) {
            setDuplicateDateEnd(new Date(data.duration.dateend));
          }
        },
        (err) => {
          console.error(err);
          setIsWaiting(false);
        },
        { skipCache: true }
      );
    }
  }, [token, objID]);

  useEffect(() => {
    if (!missionData) return;

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const startStr = missionData.duration?.datestart?.slice(0, 10);
    setIsStartToday(todayStr === startStr);

    ApiDatabase.getReasonsMissionSuspension(
      { type: 'interruption' },
      (data) => {
        let sorted = [...data];
        sorted.sort((a, b) => (a.name === 'No-Show' ? -1 : b.name === 'No-Show' ? 1 : 0));
        setReasons(sorted);
        if (sorted.length > 0) {
          setReasonSelected(sorted[0].name);
        }
        setIsWaiting(false);
      },
      (err) => {
        console.error(err);
        setIsWaiting(false);
      }
    );
  }, [missionData]);

  useEffect(() => {
    if (duplicateMission) {
      setDuplicateDateStart(endDate);
    }
  }, [endDate, duplicateMission]);

  const isNoShow = reasonSelected === 'No-Show';
  const noShowError =
    isNoShow && !isStartToday
      ? 'Le motif "No-Show" ne peut être sélectionné que le premier jour de mission'
      : '';

  const isFormValid =
    !!reasonSelected &&
    !noShowError &&
    !isWaiting &&
    (isNoShow || !!endDate) &&
    (!duplicateMission || isNoShow || (!!duplicateDateStart && !!duplicateDateEnd));

  const handleSubmit = () => {
    if (!token || !objID || !reasonSelected) {
      toast.error('Veuillez sélectionner un motif');
      return;
    }
    if (noShowError) return;

    setIsLoading(true);
    const payload = {
      token,
      idMission: objID,
      newStatus: 'interrupted',
      reason: reasonSelected,
      duplicateMission,
    };
    if (!isNoShow) {
      payload.endDate = dateToIsoString(endDate);
      if (duplicateMission) {
        payload.duplicateDateStart = dateToIsoString(duplicateDateStart);
        payload.duplicateDateEnd = dateToIsoString(duplicateDateEnd);
      }
    }

    ApiDatabase.updateMissionStatus(
      payload,
      () => {
        const isNoShowUnassign = isNoShow && duplicateMission;
        const isNoShowCancel = isNoShow && !duplicateMission;
        if (isNoShowUnassign) {
          updateMissionStatusLocal(objID, { user_assigned: null, user: null });
        } else if (isNoShowCancel) {
          updateMissionStatusLocal(objID, { status: 'canceled', reason: reasonSelected });
        } else {
          updateMissionStatusLocal(objID, { status: 'interrupted', reason: reasonSelected });
        }
        setModalDataReturn('mission_status_updated');
        toast.success('La mission a été interrompue');
        setIsLoading(false);
        toggle();
      },
      (err) => {
        toast.error("Erreur lors de l'interruption : " + err);
        setIsLoading(false);
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Interrompre la mission
      </Modalheader>
      <ModalBody>
        {isWaiting ? (
          <div className="flex flex-col gap-3 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-10 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-10 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-48" />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p>Veuillez indiquer le motif d'interruption de la mission</p>
            <StyledInput
              type="select"
              label="Motif"
              placeholder="Sélectionnez un motif"
              value={reasonSelected}
              onChange={(e) => setReasonSelected(e.target.value)}
              list={reasons.map((r) => ({ value: r.name, label: r.name }))}
              error={noShowError}
            />
            {!isNoShow && (
              <div>
                <label className="ft-sm text-gray-500">
                  Date de fin
                  <DatePicker
                    dateFormat="dd/MM/yyyy"
                    locale="fr-FR"
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    popperPlacement="auto"
                    popperProps={{ strategy: 'fixed' }}
                    popperClassName="z-[9999]"
                    className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center"
                  />
                </label>
              </div>
            )}
            <label className="flex gap-3 items-start border border-gray-200 rounded-lg p-6 cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={duplicateMission}
                onChange={(e) => setDuplicateMission(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300"
              />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800">Remplacer l'intérimaire</span>
                <span className="text-sm text-gray-500">Le client a demandé à ce que l'intérimaire soit remplacé pour cette mission</span>
              </div>
            </label>
            {!isNoShow && duplicateMission && (
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="ft-sm text-gray-500">
                    Date de début
                    <DatePicker
                      dateFormat="dd/MM/yyyy"
                      locale="fr-FR"
                      selected={duplicateDateStart}
                      onChange={(date) => setDuplicateDateStart(date)}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      popperPlacement="auto"
                      popperProps={{ strategy: 'fixed' }}
                      popperClassName="z-[9999]"
                      className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center"
                    />
                  </label>
                </div>
                <div className="flex-1">
                  <label className="ft-sm text-gray-500">
                    Date de fin
                    <DatePicker
                      dateFormat="dd/MM/yyyy"
                      locale="fr-FR"
                      selected={duplicateDateEnd}
                      onChange={(date) => setDuplicateDateEnd(date)}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      popperPlacement="auto"
                      popperProps={{ strategy: 'fixed' }}
                      popperClassName="z-[9999]"
                      className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={toggle}>
          Annuler
        </Button>
        {isLoading ? (
          <Button size="LG" color="DISABLED">
            Confirmation ...
          </Button>
        ) : (
          <Button
            size="LG"
            color={isFormValid ? "PRIMARY" : "DEFAULT"}
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalMissionInterrupt;
